// frontend/src/lib/hyphenation.ts
// Does `hyphens: auto` actually DO anything in this browser?
//
// The slip opinion is set justified, and at a phone measure justification is
// defensible only because hyphenation is carrying it. Measured on three
// paragraphs of Blackmun at a 358px column and 16px (round-5 research,
// reports/round5/research-phone.md): justified WITH hyphenation holds a median
// word-space stretch of 1.45x with 3 lines of 35 past 2x; the same text
// justified WITHOUT it goes to 1.60x, 12 lines of 36 past 2x, and a worst line
// at 3.90x, which is a gap you can put a thumb through. Hyphenation fires on
// only 5 of 35 line ends. Five hyphens buy the whole thing.
//
// `@supports (hyphens: auto)` cannot answer the question. The property can be
// supported and inert: in Chromium 125, which is what this repo's own
// committed probe runs (frontend/bin/uxprobe.mjs -> @playwright/test 1.44),
// `hyphens: auto` and `hyphens: none` produce byte-identical line breaking. A
// missing dictionary is invisible to a feature query. It is visible only to a
// measurement.
//
// So measure it. Two identical narrow boxes of long words, one hyphenating. If
// the hyphenating one is TALLER, the words were broken and the dictionary is
// there. One forced layout of two 70px divs, once, at boot.
//
// The default is the safe one, and it is that way round on purpose: the
// stylesheet sets the opinion ragged and justifies it only when this class is
// present, so a browser that cannot hyphenate, or that never runs this at all,
// is never set in the variant with the rivers.

/** Set on <html> when the browser was measured actually hyphenating. */
export const HYPHENATION_CLASS = 'hyphenates';

// Three words with no short break candidates: they either hyphenate or they
// overflow the box on a line of their own. "extraordinarily" alone is enough,
// but three of them make the height difference several lines rather than one,
// so a sub-pixel rounding difference cannot decide the answer.
const PROBE_TEXT = 'constitutional interpretation extraordinarily';

function probeBox(doc: Document, mode: 'auto' | 'none'): HTMLDivElement {
  const d = doc.createElement('div');
  // lang matters: a hyphenation dictionary is chosen by language, and an
  // element with no language inherits <html lang="en">. Stated anyway, so the
  // probe answers for en even if it is ever run against a document that does
  // not set one.
  d.lang = 'en';
  d.setAttribute('aria-hidden', 'true');
  // Which of the two boxes this is, readable without parsing a style
  // attribute. The test stubs the measurement off this, and so does anyone
  // debugging the probe in a live page.
  d.dataset.hyphenProbe = mode;
  d.style.cssText =
    'position:absolute;left:-9999px;top:0;width:70px;font:16px serif;'
    + `line-height:1.2;hyphens:${mode};-webkit-hyphens:${mode}`;
  d.textContent = PROBE_TEXT;
  doc.body.appendChild(d);
  return d;
}

/**
 * True when this browser has a working hyphenation dictionary for English.
 *
 * Reads a height, so it forces layout. Call it once.
 */
export function browserHyphenates(doc: Document = document): boolean {
  if (!doc.body) return false;
  const withHyphens = probeBox(doc, 'auto');
  const without = probeBox(doc, 'none');
  const hyphenates =
    withHyphens.getBoundingClientRect().height
    > without.getBoundingClientRect().height;
  withHyphens.remove();
  without.remove();
  return hyphenates;
}

/**
 * Measure once at boot and record the answer on <html>, where the stylesheet
 * can reach it. Returns what it found, so a caller can assert on it.
 */
export function initHyphenation(doc: Document = document): boolean {
  const hyphenates = browserHyphenates(doc);
  doc.documentElement.classList.toggle(HYPHENATION_CLASS, hyphenates);
  return hyphenates;
}
